import React, { useState, useEffect } from "react";
import { Notification } from "../NotificationLoginComponent";
import "./CrearEncuestasComponent.css";
import { useParams, useNavigate } from "react-router-dom";
import { AddPoll, GetPollById, UpdatePoll } from "../../Backend/ApiCalls";
import { BottomNav } from "../BottomNav";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  FaArrowLeft,
  FaTrash,
  FaCopy,
  FaChevronUp,
  FaChevronDown,
  FaPlus,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

/**
 * Desanida la propiedad text hasta devolver un string.
 * Si no llega a string, retorna ''.
 */
function flattenText(obj) {
  let current = obj;
  while (current && typeof current === "object" && current.text) {
    current = current.text;
  }
  return typeof current === "string" ? current : "";
}

function normalizeDateForInput(d) {
  if (!d) return "";
  if (typeof d === "string") return d.includes("T") ? d.split("T")[0] : d.slice(0, 10);
  try {
    const iso = new Date(d).toISOString();
    return iso.split("T")[0];
  } catch {
    return "";
  }
}

function normalizeTimeForInput(t) {
  if (!t) return "";
  return String(t).slice(0, 5);
}

function parseJsonSafe(v, fallback = null) {
  try {
    if (v == null) return fallback;
    if (typeof v === "string") return JSON.parse(v);
    if (typeof v === "object") return v;
    return fallback;
  } catch {
    return fallback;
  }
}

function eurosToCents(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function centsToEurosString(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return "";
  return String((n / 100).toFixed(2));
}

function getOptionAmountCents(opt) {
  if (typeof opt?.amountCents === "number") return opt.amountCents;
  if (opt?.text && typeof opt.text.amountCents === "number") return opt.text.amountCents;
  return 0;
}

function getOptionPricesByAgeType(opt) {
  const existing = opt?.pricesByAgeType || opt?.text?.pricesByAgeType || null;
  if (existing && typeof existing === "object") {
    return {
      "0": Number(existing["0"] || 0),
      "1": Number(existing["1"] || 0),
      "2": Number(existing["2"] || 0),
      "3": Number(existing["3"] || 0),
    };
  }

  const fallback = getOptionAmountCents(opt);
  return {
    "0": fallback,
    "1": fallback,
    "2": fallback,
    "3": fallback,
  };
}

const hasAnyOptionPrice = (opt) => {
  const prices = opt?.pricesByAgeType || {};
  return ["0", "1", "2", "3"].some((key) => Number(prices[key] || 0) > 0)
    || Number(opt?.amountCents || eurosToCents(opt?.priceInput)) > 0;
};

const AGE_LABELS = [
  ["0", "Exento"],
  ["1", "Infantil"],
  ["2", "Juvenil"],
  ["3", "Adulto"],
];

export const CrearEncuestaComponent = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [surveyData, setSurveyData] = useState({
    title: "",
    description: "",
    location: "",
    date: "",
    time: "",
    closeDate: "",
    closeTime: "",
    questions: [],
    isPublished: false,

    // ====== PAGO (NUEVO) ======
    paymentEnabled: false,
    manualPaymentWindows: [],
    priceExento: "",
    priceInfantil: "",
    priceJuvenil: "",
    priceAdulto: "",
  });

  const [surveyType, setSurveyType] = useState("signup");
  const [numQuestions, setNumQuestions] = useState(0);

  const [errorMessage, setErrorMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setErrorNoti] = useState(false);

  // Estado puramente visual: no altera datos ni el envío del formulario.
  // Solo controla cuándo mostramos las ayudas de validación junto a cada campo.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchPoll = async () => {
      try {
        const { data, response } = await GetPollById(id);
        if (response && !response.ok) throw new Error("GetPollById failed");

        // Preguntas
        let questions = data?.questions ?? [];
        if (typeof questions === "string") {
          try {
            questions = JSON.parse(questions);
          } catch {
            questions = [];
          }
        }
        if (!Array.isArray(questions)) questions = [];

        questions.forEach((question) => {
          if (question?.options && Array.isArray(question.options)) {
            question.options = question.options.map((opt, idx) => {
              const plainText = flattenText(opt?.text);

              let capacity = 0;
              if (opt?.text && typeof opt.text.capacity === "number") capacity = opt.text.capacity;
              else if (typeof opt?.capacity === "number") capacity = opt.capacity;

              return {
                id: opt?.id ?? (idx + 1),
                text: plainText,
                capacity,
                amountCents: getOptionAmountCents(opt),
                pricesByAgeType: getOptionPricesByAgeType(opt),
              };
            });
          } else {
            question.options = Array.isArray(question.options) ? question.options : [];
          }
        });

        // Pago
        const payCfg = parseJsonSafe(data?.paymentConfig, null);
        const prices = payCfg?.pricesByAgeType || {};
        const manualPaymentWindows = Array.isArray(payCfg?.manualPaymentWindows) ? payCfg.manualPaymentWindows : [];

        const dateString = normalizeDateForInput(data?.date);
        const closeDateString = normalizeDateForInput(data?.closeDate);

        setSurveyData((prev) => ({
          ...prev,
          title: data?.title ?? "",
          description: data?.description ?? "",
          location: data?.location ?? "",
          date: dateString,
          time: normalizeTimeForInput(data?.time),
          closeDate: closeDateString,
          closeTime: normalizeTimeForInput(data?.closeTime),
          questions: questions,
          isPublished: Number(data?.isPublished) === 1,

          paymentEnabled: Boolean(payCfg?.enabled),
          manualPaymentWindows: manualPaymentWindows.filter((item) => item?.date && item?.startTime && item?.endTime),

          // ageType: 0 exento, 1 infantil, 2 juvenil, 3 adulto
          priceExento: centsToEurosString(prices["0"]),
          priceInfantil: centsToEurosString(prices["1"]),
          priceJuvenil: centsToEurosString(prices["2"]),
          priceAdulto: centsToEurosString(prices["3"]),
        }));

        setNumQuestions(Array.isArray(questions) ? questions.length : 0);
        setSurveyType(questions && questions.length > 0 ? "questionnaire" : "signup");
      } catch (error) {
        console.error("Error fetching poll data", error);
      }
    };

    fetchPoll();
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSurveyData((prev) => ({ ...prev, [name]: value }));
  };

  const addManualPaymentWindow = () => setSurveyData((prev) => ({
    ...prev, manualPaymentWindows: [...prev.manualPaymentWindows, { date: '', startTime: '', endTime: '' }],
  }));
  const updateManualPaymentWindow = (index, field, value) => setSurveyData((prev) => ({
    ...prev, manualPaymentWindows: prev.manualPaymentWindows.map((item, i) => i === index ? { ...item, [field]: value } : item),
  }));
  const removeManualPaymentWindow = (index) => setSurveyData((prev) => ({
    ...prev, manualPaymentWindows: prev.manualPaymentWindows.filter((_, i) => i !== index),
  }));

  const handleSurveyTypeChange = (e) => {
    const next = e.target.value;
    setSurveyType(next);
    setNumQuestions(0);
    setSurveyData((prev) => ({
      ...prev,
      questions: [],
    }));
  };

  // Preguntas
  const addQuestion = () => {
    setNumQuestions((n) => n + 1);
    setSurveyData((prev) => ({
      ...prev,
      questions: [...prev.questions, { type: "singleChoice", text: "", options: [] }],
    }));
  };

  const questionHasContent = (question) =>
    Boolean(String(question?.text || "").trim()) ||
    (question?.options || []).some((opt) => String(opt?.text || "").trim());

  const removeQuestion = (index) => {
    setNumQuestions((n) => Math.max(0, n - 1));
    setSurveyData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  // Pide confirmación solo si la pregunta ya tiene contenido (texto u opciones
  // rellenadas), para no molestar cuando se borra una tarjeta recién añadida y vacía.
  const requestRemoveQuestion = (index) => {
    const question = surveyData.questions[index];
    if (!questionHasContent(question)) {
      removeQuestion(index);
      return;
    }
    confirmAlert({
      title: "Eliminar pregunta",
      message: `Esta pregunta tiene contenido. ¿Seguro que quieres eliminar la pregunta ${index + 1}?`,
      buttons: [
        { label: "Sí, eliminar", onClick: () => removeQuestion(index) },
        { label: "Cancelar", onClick: () => {} },
      ],
    });
  };

  const duplicateQuestion = (index) => {
    setSurveyData((prev) => {
      const original = prev.questions[index];
      const clone = {
        ...original,
        options: (original.options || []).map((opt) => ({
          ...opt,
          pricesByAgeType: { ...(opt.pricesByAgeType || {}) },
        })),
      };
      const questions = [...prev.questions];
      questions.splice(index + 1, 0, clone);
      return { ...prev, questions };
    });
    setNumQuestions((n) => n + 1);
  };

  const moveQuestion = (index, direction) => {
    setSurveyData((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      const [moved] = questions.splice(index, 1);
      questions.splice(target, 0, moved);
      return { ...prev, questions };
    });
  };

  const handleQuestionTextChange = (index, text) => {
    setSurveyData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, text } : q)),
    }));
  };

  const handleQuestionTypeChange = (index, type) => {
    setSurveyData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, type, options: type === "text" ? [] : (q.options || []) } : q
      ),
    }));
  };

  const addOption = (questionIndex) => {
    setSurveyData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex
          ? { ...q, options: [...(q.options || []), { text: "", capacity: 0, pricesByAgeType: { "0": 0, "1": 0, "2": 0, "3": 0 } }] }
          : q
      ),
    }));
  };

  const removeOptionAt = (questionIndex, optionIndex) => {
    setSurveyData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex
          ? { ...q, options: (q.options || []).filter((_, j) => j !== optionIndex) }
          : q
      ),
    }));
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    setSurveyData((prev) => {
      const updated = [...prev.questions];
      updated[questionIndex].options[optionIndex].text = value;
      return { ...prev, questions: updated };
    });
  };

  const handleOptionCapacityChange = (questionIndex, optionIndex, value) => {
    const cap = parseInt(value, 10);
    setSurveyData((prev) => {
      const updated = [...prev.questions];
      updated[questionIndex].options[optionIndex].capacity = Number.isFinite(cap) ? cap : 0;
      return { ...prev, questions: updated };
    });
  };

  const handleOptionAgePriceChange = (questionIndex, optionIndex, ageType, value) => {
    setSurveyData((prev) => {
      const updated = [...prev.questions];
      const currentPrices = updated[questionIndex].options[optionIndex].pricesByAgeType || {};
      updated[questionIndex].options[optionIndex].pricesByAgeType = {
        ...currentPrices,
        [String(ageType)]: eurosToCents(value),
      };
      return { ...prev, questions: updated };
    });
  };

  // Rellena el precio para las 4 edades a la vez. Si quieres cobrar lo mismo
  // a todo el mundo, este es el atajo: rellenas un único campo y se propaga.
  const handleOptionCommonPriceChange = (questionIndex, optionIndex, value) => {
    const cents = eurosToCents(value);
    setSurveyData((prev) => {
      const updated = [...prev.questions];
      updated[questionIndex].options[optionIndex].pricesByAgeType = {
        "0": cents,
        "1": cents,
        "2": cents,
        "3": cents,
      };
      return { ...prev, questions: updated };
    });
  };

  // Devuelve el precio común si los 4 ages tienen exactamente el mismo valor;
  // en caso contrario devuelve "" (el admin tiene precios distintos por edad).
  const getCommonOptionPrice = (option) => {
    const prices = option?.pricesByAgeType || {};
    const values = ["0", "1", "2", "3"].map((key) => Number(prices[key] || 0));
    const first = values[0];
    const allEqual = values.every((v) => v === first);
    return allEqual && first > 0 ? first : 0;
  };

  const validateBeforeSubmit = () => {
    if (!surveyData.title?.trim()) return "Falta el título";
    if (!surveyData.description?.trim()) return "Falta la descripción";
    if (!surveyData.date) return "Falta la fecha";
    if (!surveyData.time) return "Falta la hora";

    if (surveyData.isPublished) {
      if (!surveyData.closeDate) return "Si la encuesta está publicada, falta la fecha de cierre";
      if (!surveyData.closeTime) return "Si la encuesta está publicada, falta la hora de cierre";
    }

    if (surveyData.paymentEnabled) {
      if (surveyData.manualPaymentWindows.some((item) => !/^\d{4}-\d{2}-\d{2}$/.test(item.date) || !/^\d{2}:\d{2}$/.test(item.startTime) || !/^\d{2}:\d{2}$/.test(item.endTime) || item.endTime <= item.startTime)) return 'Revisa las franjas de pago presencial.';
      const anyPrice = surveyType === "questionnaire"
        ? surveyData.questions.some((q) =>
            (q.options || []).some((opt) => hasAnyOptionPrice(opt))
          )
        : (
            eurosToCents(surveyData.priceExento) > 0 ||
            eurosToCents(surveyData.priceInfantil) > 0 ||
            eurosToCents(surveyData.priceJuvenil) > 0 ||
            eurosToCents(surveyData.priceAdulto) > 0
          );
      if (!anyPrice) return "Has activado el pago pero todos los precios están a 0.";
    }

    // Validaciones de contenido mínimo de preguntas/opciones. Antes no existían
    // y permitían guardar preguntas u opciones en blanco que luego el
    // participante veía vacías al responder.
    if (surveyType === "questionnaire") {
      for (let i = 0; i < surveyData.questions.length; i++) {
        const q = surveyData.questions[i];
        if (!String(q.text || "").trim()) return `La pregunta ${i + 1} no tiene texto`;
        if (q.type !== "text") {
          const options = q.options || [];
          if (options.length === 0) return `La pregunta ${i + 1} no tiene opciones`;
          const emptyOptionIndex = options.findIndex((opt) => !String(opt.text || "").trim());
          if (emptyOptionIndex !== -1) {
            return `La opción ${emptyOptionIndex + 1} de la pregunta ${i + 1} está vacía`;
          }
        }
      }
    }

    return "";
  };

  // Checklist de la sección "Revisión antes de publicar". Es puramente
  // informativo: refleja las mismas condiciones que validateBeforeSubmit
  // pero no bloquea nada por sí mismo ni cambia el envío del formulario.
  const getReviewChecklist = () => {
    const items = [];

    items.push({
      ok: Boolean(surveyData.title?.trim()),
      label: "Título de la encuesta",
    });
    items.push({
      ok: Boolean(surveyData.description?.trim()),
      label: "Descripción de la encuesta",
    });
    items.push({
      ok: Boolean(surveyData.date && surveyData.time),
      label: "Fecha y hora del evento",
    });

    if (surveyData.isPublished) {
      items.push({
        ok: Boolean(surveyData.closeDate && surveyData.closeTime),
        label: "Fecha y hora de cierre (obligatorias al publicar)",
      });
    }

    if (surveyType === "questionnaire") {
      items.push({
        ok: surveyData.questions.length > 0,
        label: "Al menos una pregunta",
      });
      const allQuestionsOk = surveyData.questions.every((q) => {
        if (!String(q.text || "").trim()) return false;
        if (q.type !== "text") {
          const options = q.options || [];
          if (options.length === 0) return false;
          return options.every((opt) => String(opt.text || "").trim());
        }
        return true;
      });
      items.push({
        ok: surveyData.questions.length === 0 ? true : allQuestionsOk,
        label: "Todas las preguntas y opciones tienen texto",
      });
    }

    if (surveyData.paymentEnabled) {
      const anyPrice = surveyType === "questionnaire"
        ? surveyData.questions.some((q) => (q.options || []).some((opt) => hasAnyOptionPrice(opt)))
        : (
            eurosToCents(surveyData.priceExento) > 0 ||
            eurosToCents(surveyData.priceInfantil) > 0 ||
            eurosToCents(surveyData.priceJuvenil) > 0 ||
            eurosToCents(surveyData.priceAdulto) > 0
          );
      items.push({
        ok: anyPrice,
        label: "Al menos un importe mayor que 0 EUR",
      });
    }

    return items;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    const validationErr = validateBeforeSubmit();
    if (validationErr) {
      setErrorMessage(validationErr);
      setErrorNoti(true);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);
      return;
    }

    const indexedQuestions =
      surveyType === "signup"
        ? []
        : surveyData.questions.map((q, i) => ({
            ...q,
            id: i + 1,
            options: (q.options || []).map((opt, j) => ({
              id: j + 1,
              text: opt.text,
              capacity: opt.capacity || 0,
              // amountCents queda como precio legacy de referencia; el motor
              // de pago real usa pricesByAgeType[ageType] estricto.
              amountCents: Number(opt.amountCents || eurosToCents(opt.priceInput)),
              pricesByAgeType: {
                "0": Number(opt.pricesByAgeType?.["0"] || 0),
                "1": Number(opt.pricesByAgeType?.["1"] || 0),
                "2": Number(opt.pricesByAgeType?.["2"] || 0),
                "3": Number(opt.pricesByAgeType?.["3"] || 0),
              },
            })),
          }));

    const paymentConfig = surveyData.paymentEnabled
      ? {
          enabled: true,
          currency: "EUR",
          pricingMode: surveyType === "questionnaire" ? "question_options" : "age_type",
          pricesByAgeType: surveyType === "questionnaire"
            ? {}
            : {
                "0": eurosToCents(surveyData.priceExento),
                "1": eurosToCents(surveyData.priceInfantil),
                "2": eurosToCents(surveyData.priceJuvenil),
                "3": eurosToCents(surveyData.priceAdulto),
              },
          label: surveyData.title,
          manualPaymentWindows: surveyData.manualPaymentWindows.filter((item) => item.date && item.startTime && item.endTime).sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`)),
        }
      : null;

    const dataToSubmit = {
      ...surveyData,
      time: normalizeTimeForInput(surveyData.time),
      closeTime: normalizeTimeForInput(surveyData.closeTime),
      questions: indexedQuestions,
      paymentConfig,
    };

    try {
      if (isEditMode) {
        const { response, data } = await UpdatePoll({ id, ...dataToSubmit });
        if (!response?.ok) throw new Error(data?.Error || "Error editing survey");
        setErrorMessage(props.I18n?.SuccessMessageEditEvent || "Survey updated successfully");
        setErrorNoti(false);
      } else {
        const { response, data } = await AddPoll(dataToSubmit);
        if (!response?.ok) throw new Error(data?.Error || "Error creating survey");

        setErrorMessage(props.I18n?.SuccessMessageCreateEvent || "Survey created successfully");
        setErrorNoti(false);

        setSurveyData({
          title: "",
          description: "",
          location: "",
          date: "",
          time: "",
          closeDate: "",
          closeTime: "",
          questions: [],
          isPublished: false,

          paymentEnabled: false,
          priceExento: "",
          priceInfantil: "",
          priceJuvenil: "",
          priceAdulto: "",
        });
        setSurveyType("signup");
        setNumQuestions(0);
        setSubmitAttempted(false);
      }
    } catch (error) {
      console.error("Error creating/updating poll", error);
      setErrorMessage(
        isEditMode
          ? (props.I18n?.ErrorMessageEditEvent || "Error editing survey")
          : (props.I18n?.ErrorMessageCreateEvent || "Error creating survey")
      );
      setErrorNoti(true);
    }

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 5000);
  };

  const showFieldError = (condition) => submitAttempted && condition;
  const reviewChecklist = getReviewChecklist();
  const pendingReviewCount = reviewChecklist.filter((item) => !item.ok).length;

  return (
    <div className="create-survey-container">
      {showNotification && (
        <Notification message={errorMessage} type={isErrorNoti ? "error" : "success"} />
      )}

      <button className="back-button" onClick={() => navigate("/gestionar-encuestas")}>
        <FaArrowLeft className="back-icon" /> Volver
      </button>

      <BottomNav I18n={props.I18n} user={props.user} />

      <div className="recipient">
        <h1 className="titulo-page">
          {isEditMode ? props.I18n?.EditSurvey : props.I18n?.CreateSurveys}
        </h1>
        <p className="create-survey-subtitle">
          Rellena las secciones y revisa el resumen final antes de publicar.
        </p>

        <form className="create-survey-form-v2" onSubmit={handleSubmit} noValidate>

          {/* ====== 1. INFORMACIÓN BÁSICA ====== */}
          <section className="survey-section">
            <header className="survey-section-header">
              <span className="survey-section-badge">1</span>
              <div>
                <h2>Información básica</h2>
                <p>Cómo se llama la encuesta y de qué trata</p>
              </div>
            </header>

            <div className="survey-section-body">
              <div className="survey-type-row">
                <label className={`radio-pill${surveyType === "signup" ? " is-active" : ""}`}>
                  <input
                    className="create-survey-radio"
                    type="radio"
                    name="surveyType"
                    value="signup"
                    checked={surveyType === "signup"}
                    onChange={handleSurveyTypeChange}
                  />
                  {props.I18n?.SignUpForEventAlone || "Inscripción simple"}
                </label>

                <label className={`radio-pill${surveyType === "questionnaire" ? " is-active" : ""}`}>
                  <input
                    className="create-survey-radio"
                    type="radio"
                    name="surveyType"
                    value="questionnaire"
                    checked={surveyType === "questionnaire"}
                    onChange={handleSurveyTypeChange}
                  />
                  {props.I18n?.CreateQuiz || "Cuestionario"}
                </label>
              </div>

              <label className="field">
                {props.I18n?.Qualification || "Título"}
                <input
                  className="create-survey-input"
                  type="text"
                  name="title"
                  value={surveyData.title}
                  onChange={handleInputChange}
                />
                {showFieldError(!surveyData.title?.trim()) && (
                  <span className="field-error">Falta el título</span>
                )}
              </label>

              <label className="field">
                {props.I18n?.Description || "Descripción"}
                <textarea
                  className="create-survey-input"
                  name="description"
                  rows={4}
                  value={surveyData.description}
                  onChange={handleInputChange}
                />
                {showFieldError(!surveyData.description?.trim()) && (
                  <span className="field-error">Falta la descripción</span>
                )}
              </label>

              <label className="field">
                {props.I18n?.Place || "Lugar"} <span className="field-optional-tag">(opcional)</span>
                <input
                  className="create-survey-input"
                  type="text"
                  name="location"
                  value={surveyData.location}
                  onChange={handleInputChange}
                />
              </label>
            </div>
          </section>

          {/* ====== 2. PREGUNTAS Y OPCIONES ====== */}
          <section className="survey-section">
            <header className="survey-section-header">
              <span className="survey-section-badge">2</span>
              <div>
                <h2>Preguntas y opciones</h2>
                <p>
                  {surveyType === "signup"
                    ? "No aplica: es una inscripción simple sin preguntas"
                    : `${surveyData.questions.length} pregunta${surveyData.questions.length === 1 ? "" : "s"}`}
                </p>
              </div>
            </header>

            <div className="survey-section-body">
              {surveyType === "signup" ? (
                <p className="survey-hint">
                  Cambia el tipo a "Cuestionario" en la sección 1 si necesitas añadir preguntas.
                </p>
              ) : (
                <>
                  <button type="button" className="create-survey-button add-question-btn" onClick={addQuestion}>
                    <FaPlus /> {props.I18n?.AddQuestion || "Añadir pregunta"}
                  </button>

                  {surveyData.questions.length === 0 && (
                    <p className="survey-hint">Todavía no has añadido ninguna pregunta.</p>
                  )}

                  {surveyData.questions.map((question, index) => (
                    <div key={index} className="question-card">
                      <div className="question-card-toolbar">
                        <span className="question-number-badge">Pregunta {index + 1}</span>
                        <div className="question-card-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            title="Subir pregunta"
                            aria-label="Mover pregunta hacia arriba"
                            onClick={() => moveQuestion(index, -1)}
                            disabled={index === 0}
                          >
                            <FaChevronUp /> <span>Subir</span>
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            title="Bajar pregunta"
                            aria-label="Mover pregunta hacia abajo"
                            onClick={() => moveQuestion(index, 1)}
                            disabled={index === surveyData.questions.length - 1}
                          >
                            <FaChevronDown /> <span>Bajar</span>
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            title="Duplicar pregunta"
                            aria-label="Duplicar pregunta"
                            onClick={() => duplicateQuestion(index)}
                          >
                            <FaCopy /> <span>Duplicar</span>
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn-danger"
                            title="Eliminar pregunta"
                            aria-label="Eliminar pregunta"
                            onClick={() => requestRemoveQuestion(index)}
                          >
                            <FaTrash /> <span>Eliminar</span>
                          </button>
                        </div>
                      </div>

                      <div className="grid-2">
                        <label className="field">
                          {props.I18n?.Ask || "Pregunta"}
                          <input
                            className="create-survey-input"
                            type="text"
                            value={question.text}
                            onChange={(e) => handleQuestionTextChange(index, e.target.value)}
                          />
                          {showFieldError(!String(question.text || "").trim()) && (
                            <span className="field-error">Esta pregunta no tiene texto</span>
                          )}
                        </label>

                        <label className="field">
                          Tipo de respuesta
                          <select
                            className="create-survey-select"
                            value={question.type}
                            onChange={(e) => handleQuestionTypeChange(index, e.target.value)}
                          >
                            <option value="singleChoice">{props.I18n?.OnlyAnswer || "Una respuesta"}</option>
                            <option value="multiChoice">{props.I18n?.VariousAnswers || "Varias respuestas"}</option>
                            <option value="text">{props.I18n?.TextAnswer || "Respuesta texto"}</option>
                          </select>
                        </label>
                      </div>

                      {question.type !== "text" && (
                        <div className="question-options-block">
                          <span className="question-options-title">Opciones</span>

                          {(question.options || []).length === 0 && (
                            <p className="survey-hint">Añade al menos una opción de respuesta.</p>
                          )}

                          {(question.options || []).map((option, optionIndex) => (
                            <div key={optionIndex} className="option-edit-card">
                              <div className="option-edit-grid">
                                <label className="field">
                                  {props.I18n?.Option || "Opción"} {optionIndex + 1}
                                  <input
                                    className="create-survey-input"
                                    type="text"
                                    value={option.text}
                                    onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                  />
                                  {showFieldError(!String(option.text || "").trim()) && (
                                    <span className="field-error">Esta opción está vacía</span>
                                  )}
                                </label>

                                <label className="field">
                                  Cupo máximo
                                  <input
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    className="create-survey-input"
                                    value={option.capacity ?? ""}
                                    onChange={(e) => handleOptionCapacityChange(index, optionIndex, e.target.value)}
                                  />
                                </label>

                                <button
                                  type="button"
                                  className="icon-btn icon-btn-danger option-remove-btn"
                                  title="Eliminar esta opción"
                                  aria-label={`Eliminar opción ${optionIndex + 1}`}
                                  onClick={() => removeOptionAt(index, optionIndex)}
                                >
                                  <FaTrash /> <span>Eliminar opción</span>
                                </button>
                              </div>

                              {surveyData.paymentEnabled && (
                                <div className="option-age-prices">
                                  <span className="option-age-prices-title">Precio (EUR)</span>
                                  <label className="field">
                                    Precio común para todas las edades
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      inputMode="decimal"
                                      className="create-survey-input"
                                      placeholder="Cobrar lo mismo a todos"
                                      value={centsToEurosString(getCommonOptionPrice(option))}
                                      onChange={(e) => handleOptionCommonPriceChange(index, optionIndex, e.target.value)}
                                    />
                                  </label>
                                  <span className="option-age-prices-title">
                                    …o un precio distinto por edad (sobrescribe el común)
                                  </span>
                                  <div className="option-age-prices-grid">
                                    {AGE_LABELS.map(([ageType, label]) => (
                                      <label className="field" key={ageType}>
                                        {label}
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          inputMode="decimal"
                                          className="create-survey-input"
                                          value={centsToEurosString(option.pricesByAgeType?.[ageType])}
                                          onChange={(e) => handleOptionAgePriceChange(index, optionIndex, ageType, e.target.value)}
                                        />
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          <button type="button" className="create-survey-button add-option-btn" onClick={() => addOption(index)}>
                            <FaPlus /> {props.I18n?.AddOption || "Añadir opción"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </section>

          {/* ====== 3. PARTICIPANTES / DESTINATARIOS ====== */}
          <section className="survey-section survey-section-info">
            <header className="survey-section-header">
              <span className="survey-section-badge">3</span>
              <div>
                <h2>Destinatarios</h2>
                <p>Quién verá esta encuesta</p>
              </div>
            </header>
            <div className="survey-section-body">
              <p className="survey-hint">
                Al publicarla, esta encuesta se enviará a todos los socios con acceso a la aplicación.
                Actualmente no existe segmentación por grupos: la visibilidad es para todos.
              </p>
            </div>
          </section>

          {/* ====== 4. FECHAS Y DISPONIBILIDAD ====== */}
          <section className="survey-section">
            <header className="survey-section-header">
              <span className="survey-section-badge">4</span>
              <div>
                <h2>Fechas y disponibilidad</h2>
                <p>Cuándo es el evento y cuándo se cierra la encuesta</p>
              </div>
            </header>

            <div className="survey-section-body">
              <div className="grid-2">
                <label className="field">
                  {props.I18n?.Date || "Fecha del evento"}
                  <input
                    className="create-survey-input"
                    type="date"
                    name="date"
                    value={surveyData.date}
                    onChange={handleInputChange}
                  />
                  {showFieldError(!surveyData.date) && (
                    <span className="field-error">Falta la fecha</span>
                  )}
                </label>

                <label className="field">
                  {props.I18n?.Hour || "Hora del evento"}
                  <input
                    className="create-survey-input"
                    type="time"
                    name="time"
                    value={surveyData.time}
                    onChange={handleInputChange}
                  />
                  {showFieldError(!surveyData.time) && (
                    <span className="field-error">Falta la hora</span>
                  )}
                </label>
              </div>

              <div className="close-box">
                <div className="close-title">Cierre de la encuesta</div>
                <p className="survey-hint">
                  Se envía un recordatorio 24 h antes de esta fecha. Obligatorio solo si publicas la encuesta ahora.
                </p>
                <div className="grid-2">
                  <label className="field">
                    Fecha de cierre
                    <input
                      className="create-survey-input"
                      type="date"
                      name="closeDate"
                      value={surveyData.closeDate}
                      onChange={handleInputChange}
                    />
                    {showFieldError(surveyData.isPublished && !surveyData.closeDate) && (
                      <span className="field-error">Falta la fecha de cierre</span>
                    )}
                  </label>

                  <label className="field">
                    Hora de cierre
                    <input
                      className="create-survey-input"
                      type="time"
                      name="closeTime"
                      value={surveyData.closeTime}
                      onChange={handleInputChange}
                    />
                    {showFieldError(surveyData.isPublished && !surveyData.closeTime) && (
                      <span className="field-error">Falta la hora de cierre</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* ====== 5. CONFIGURACIÓN DE PAGO ====== */}
          <section className="survey-section">
            <header className="survey-section-header">
              <span className="survey-section-badge">5</span>
              <div>
                <h2>Pago asociado a la encuesta</h2>
                <p>{surveyData.paymentEnabled ? "Esta encuesta pide un importe" : "Encuesta sin pago"}</p>
              </div>
            </header>

            <div className="survey-section-body">
              <label className="pay-toggle">
                <input
                  type="checkbox"
                  checked={surveyData.paymentEnabled}
                  onChange={(e) =>
                    setSurveyData((p) => ({ ...p, paymentEnabled: e.target.checked }))
                  }
                />
                <span>Esta encuesta tiene pago</span>
              </label>

              {surveyData.paymentEnabled && (
                <div className="pay-box">
                  <p className="survey-hint pay-info-note">
                    Los participantes podrán pagar desde la aplicación o realizar el pago
                    posteriormente en el casal. El pago online es solo una facilidad adicional,
                    no es obligatorio para poder responder.
                  </p>

                  <p className="pay-hint">
                    Define el precio por tipo de edad. Se guarda en céntimos para trabajar con Redsys sin errores de redondeo.
                  </p>

                  {surveyType === "questionnaire" && (
                    <p className="pay-hint">
                      En cuestionarios, el importe se define dentro de cada opción de respuesta (más abajo, en la sección 2).
                      Las opciones a 0 EUR no se cobran.
                    </p>
                  )}

                  {surveyType !== "questionnaire" && (
                    <div className="pay-grid">
                      <label className="pay-field">
                        <span>Exento (€)</span>
                        <input
                          className="create-survey-input"
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          value={surveyData.priceExento}
                          onChange={(e) => setSurveyData((p) => ({ ...p, priceExento: e.target.value }))}
                        />
                      </label>

                      <label className="pay-field">
                        <span>Infantil (€)</span>
                        <input
                          className="create-survey-input"
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          value={surveyData.priceInfantil}
                          onChange={(e) => setSurveyData((p) => ({ ...p, priceInfantil: e.target.value }))}
                        />
                      </label>

                      <label className="pay-field">
                        <span>Juvenil (€)</span>
                        <input
                          className="create-survey-input"
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          value={surveyData.priceJuvenil}
                          onChange={(e) => setSurveyData((p) => ({ ...p, priceJuvenil: e.target.value }))}
                        />
                      </label>

                      <label className="pay-field">
                        <span>Adulto (€)</span>
                        <input
                          className="create-survey-input"
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          value={surveyData.priceAdulto}
                          onChange={(e) => setSurveyData((p) => ({ ...p, priceAdulto: e.target.value }))}
                        />
                      </label>
                    </div>
                  )}

                  {showFieldError(
                    surveyType === "questionnaire"
                      ? !surveyData.questions.some((q) => (q.options || []).some((opt) => hasAnyOptionPrice(opt)))
                      : !(
                          eurosToCents(surveyData.priceExento) > 0 ||
                          eurosToCents(surveyData.priceInfantil) > 0 ||
                          eurosToCents(surveyData.priceJuvenil) > 0 ||
                          eurosToCents(surveyData.priceAdulto) > 0
                        )
                  ) && (
                    <span className="field-error">Has activado el pago pero todos los precios están a 0.</span>
                  )}

                  <div className="manual-payment-windows">
                    <strong>Pago presencial en el casal</strong>
                    {surveyData.manualPaymentWindows.map((item, index) => (
                      <div className="manual-payment-window" key={`${item.date}-${index}`}>
                        <input type="date" value={item.date} onChange={(e) => updateManualPaymentWindow(index, 'date', e.target.value)} />
                        <input type="time" value={item.startTime} onChange={(e) => updateManualPaymentWindow(index, 'startTime', e.target.value)} />
                        <input type="time" value={item.endTime} onChange={(e) => updateManualPaymentWindow(index, 'endTime', e.target.value)} />
                        <button type="button" className="icon-btn icon-btn-danger" onClick={() => removeManualPaymentWindow(index)}>Eliminar</button>
                      </div>
                    ))}
                    <button type="button" className="create-survey-button add-option-btn" onClick={addManualPaymentWindow}>+ Añadir día</button>
                  </div>

                  <div className="pay-methods-preview">
                    <span className="pay-methods-preview-title">Qué verá el participante</span>
                    <div className="pay-method-chip pay-method-available">
                      Tarjeta bancaria — disponible
                    </div>
                    <div className="pay-method-chip pay-method-maintenance">
                      Bizum — en mantenimiento
                    </div>
                    <div className="pay-method-chip pay-method-maintenance">
                      Apple Pay / Google Pay — en mantenimiento
                    </div>
                    <p className="survey-hint">
                      Bizum y Apple/Google Pay se muestran como opción futura pero no se pueden usar todavía.
                      El participante siempre podrá elegir pagar presencialmente en el casal.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ====== 6. REVISIÓN ANTES DE PUBLICAR ====== */}
          <section className="survey-section survey-section-review">
            <header className="survey-section-header">
              <span className="survey-section-badge">6</span>
              <div>
                <h2>Revisión antes de publicar</h2>
                <p>Comprueba que todo esté correcto</p>
              </div>
            </header>

            <div className="survey-section-body">
              <label className="field">
                Estado de la encuesta
                <select
                  name="isPublished"
                  className="create-survey-select"
                  value={surveyData.isPublished ? "true" : "false"}
                  onChange={(e) =>
                    setSurveyData((prev) => ({
                      ...prev,
                      isPublished: e.target.value === "true",
                    }))
                  }
                >
                  <option value="false">Pendiente (no visible aún para los socios)</option>
                  <option value="true">Publicada (visible y notificada a todos)</option>
                </select>
              </label>

              <div className="review-summary-card">
                <div className="review-summary-row">
                  <span className="review-summary-label">Nombre</span>
                  <span className="review-summary-value">{surveyData.title || "—"}</span>
                </div>
                <div className="review-summary-row">
                  <span className="review-summary-label">Destinatarios</span>
                  <span className="review-summary-value">Todos los socios con acceso a la app</span>
                </div>
                <div className="review-summary-row">
                  <span className="review-summary-label">Fecha del evento</span>
                  <span className="review-summary-value">
                    {surveyData.date || "—"} {surveyData.time && `· ${surveyData.time}`}
                  </span>
                </div>
                <div className="review-summary-row">
                  <span className="review-summary-label">Cierre de la encuesta</span>
                  <span className="review-summary-value">
                    {surveyData.closeDate ? `${surveyData.closeDate} · ${surveyData.closeTime || "—"}` : "Sin definir"}
                  </span>
                </div>
                <div className="review-summary-row">
                  <span className="review-summary-label">Preguntas</span>
                  <span className="review-summary-value">
                    {surveyType === "signup" ? "Inscripción simple (sin preguntas)" : `${surveyData.questions.length} pregunta(s)`}
                  </span>
                </div>
                <div className="review-summary-row">
                  <span className="review-summary-label">Pago</span>
                  <span className="review-summary-value">
                    {surveyData.paymentEnabled
                      ? "Sí — online (opcional) o presencial en el casal"
                      : "No — encuesta gratuita"}
                  </span>
                </div>
                {surveyData.paymentEnabled && (
                  <>
                    <div className="review-summary-row">
                      <span className="review-summary-label">Métodos online</span>
                      <span className="review-summary-value">Tarjeta bancaria</span>
                    </div>
                    <div className="review-summary-row">
                      <span className="review-summary-label">En mantenimiento</span>
                      <span className="review-summary-value">Bizum, Apple Pay / Google Pay</span>
                    </div>
                  </>
                )}
              </div>

              <div className="review-checklist">
                <span className="review-checklist-title">
                  {pendingReviewCount === 0
                    ? "Todo listo para guardar"
                    : `${pendingReviewCount} punto(s) por revisar`}
                </span>
                {reviewChecklist.map((item, idx) => (
                  <div key={idx} className={`review-checklist-item${item.ok ? " is-ok" : " is-pending"}`}>
                    {item.ok ? <FaCheckCircle /> : <FaExclamationCircle />}
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="create-survey-submit-bar">
            <button type="submit" className="create-survey-button primary create-survey-submit-btn">
              {isEditMode ? props.I18n?.Update || "Actualizar encuesta" : props.I18n?.Send || "Crear encuesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
